import { Controller } from "@hotwired/stimulus"

// Client-side category filter for the blog index.
//
// Posts carry `data-categories` (space-separated slugs); pills carry a single
// `data-category`. Pills are real <a href="/blog/?category=slug"> links, so they
// stay shareable and deep-linkable: a category chip on a post page navigates to
// the index, and connect() reads ?category= and applies the filter on arrival.
// Clicks are intercepted to filter in place (no reload) and kept in sync with the
// URL via pushState, so back/forward and link-sharing both work.
export default class extends Controller {
  static targets = ["pill", "post", "featured", "empty"]
  static values = { category: { type: String, default: "all" } }

  connect() {
    this.onPopState = this.onPopState.bind(this)
    window.addEventListener("popstate", this.onPopState)
    this.apply(this.categoryFromURL(), { push: false })
  }

  disconnect() {
    window.removeEventListener("popstate", this.onPopState)
  }

  // Pill / "view all" click. preventDefault so we filter in place instead of
  // reloading; the href is still the source of truth for no-JS and middle-click.
  select(event) {
    event.preventDefault()
    this.apply(event.currentTarget.dataset.category || "all", { push: true })
  }

  onPopState() {
    this.apply(this.categoryFromURL(), { push: false })
  }

  apply(category, { push }) {
    this.categoryValue = category

    let visible = 0
    this.postTargets.forEach((post) => {
      const cats = (post.dataset.categories || "").split(" ").filter(Boolean)
      const isFeatured = post.dataset.featured === "true"
      const matchesCategory = category === "all" || cats.includes(category)
      // In the default ("all") view, the three featured posts already appear in
      // the Featured block above, so hide their duplicate rows here. Once a real
      // filter is active the Featured block is hidden, so show every match.
      const show = category === "all" ? matchesCategory && !isFeatured : matchesCategory
      post.hidden = !show
      if (show) visible += 1
    })

    this.pillTargets.forEach((pill) => {
      const active = (pill.dataset.category || "all") === category
      pill.setAttribute("aria-current", active ? "true" : "false")
    })

    // Featured is a curated "all" view; hide it once a real filter is active.
    if (this.hasFeaturedTarget) this.featuredTarget.hidden = category !== "all"

    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible !== 0

    if (push) this.syncURL(category)
  }

  categoryFromURL() {
    const value = new URLSearchParams(window.location.search).get("category")
    return value ? value.toLowerCase() : "all"
  }

  syncURL(category) {
    const url = new URL(window.location)
    if (category === "all") {
      url.searchParams.delete("category")
    } else {
      url.searchParams.set("category", category)
    }
    window.history.pushState({}, "", url)
  }
}
